import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import Draggable from "react-draggable";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { MdOutlineAddCircleOutline } from "react-icons/md";
import { TbTrash, TbTrashOff } from "react-icons/tb";
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import swal from "sweetalert";
import {
  CheckPermission,
  customOption,
  customSingleValue,
  LoadingSpinner,
} from "../../../constants/const";
import { encryptStorage } from "../../../constants/EncryptStorage";
import { ApiFinance } from "../../config/finance/ApiFinance";
import { GetLanguageString } from "../../helper/Components";
import { GetCustomStyles, compressImageFile, getFileNameWithoutExtension } from "../../helper/Method";
import { ApiFinance as ApiFinanceLocal } from "../ApiFinance";
import { openAttachmentInNewTab } from "../attachmentHelper";
import { getMinMaxDates, validateTransactionDate } from "../fiscalYearHelper";

const CustomSelect = ({
  label,
  name,
  options = [],
  value,
  onChange,
  loading,
  error,
  placeholder,
  isDisabled,
}) => (
  <FormGroup>
    <Label className="col-form-label">
      <GetLanguageString props={label} />
    </Label>
    <Select
      components={{ Option: customOption, SingleValue: customSingleValue }}
      options={options || []}
      name={name}
      value={value}
      className="basic-select no-drag"
      classNamePrefix="select"
      isLoading={loading}
      isDisabled={isDisabled || !options || options.length === 0}
      onChange={onChange}
      placeholder={placeholder}
      {...(error && { invalid: true })}
    />
    {error && <h4 className="text-error">{error}</h4>}
  </FormGroup>
);

const CustomInput = ({ label, name, value, onChange, error, readOnly, disabled }) => (
  <FormGroup>
    <Label className="col-form-label">
      <GetLanguageString props={label} />
    </Label>
    <Input
      name={name}
      type="text"
      value={value}
      onChange={onChange}
      className="no-drag"
      readOnly={readOnly}
      disabled={disabled}
    />
    {error && <h4 className="text-error no-drag">{error}</h4>}
  </FormGroup>
);

const matchesOption = (option, values) => {
  const optionValues = [
    option?.value,
    option?.id,
    option?.label,
    option?.label1,
    option?.name,
    option?.title,
    option?.code,
  ]
    .map((item) => String(item || "").trim().toLowerCase())
    .filter(Boolean);

  return values.some((value) =>
    optionValues.includes(String(value || "").trim().toLowerCase()),
  );
};

const findOption = (options, values) =>
  options.find((option) => matchesOption(option, values)) || null;

const parseExpenseMemo = (memo) => {
  const value = String(memo || "").trim();
  const match = value.match(/^[-]?(\d+)\s*x\s*(\d+)(.*)/i);

  if (!match) {
    return { memo: value, quantity: "", price: "" };
  }

  let quantity = String(parseInt(match[1], 10));
  let price = String(parseInt(match[2], 10));

  let remainder = (match[3] || "").trim();
  let memoPart = remainder.replace(/^\(|\)$/g, "").trim();

  return {
    quantity,
    price,
    memo: memoPart || "",
  };
};

const getDateValue = (value) => {
  if (!value) return new Date();
  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
};

const getAmount = (value) => {
  if (value === null || value === undefined || value === "") return "";
  return String(Math.abs(Number(String(value).replace(/,/g, ""))));
};

const cleanSerialNumber = (val) => {
  const str = String(val || "").trim();
  const match = str.match(/^(\d+)/);
  return match ? match[1] : str.replace(/\s*[-()].*$/, "").trim();
};

const isSpecialCategory = (categoryCode) => {
  return (
    categoryCode === "return_loan_individual" ||
    categoryCode === "loan_individual" ||
    categoryCode === "return_loan_from_markaz" ||
    categoryCode === "maktaba_payment_to_markaz" ||
    categoryCode === "nida_payment_to_markaz" ||
    categoryCode === "maktaba_payable" ||
    categoryCode === "nida_payable" ||
    categoryCode === "maktaba_expense" ||
    (categoryCode && categoryCode.includes("markaz"))
  );
};

const isSingleRowCategory = (categoryCode, categoryLabel = "") => {
  if (!categoryCode && !categoryLabel) return false;
  const code = (categoryCode || "").toLowerCase();
  const label = (categoryLabel || "").toLowerCase();
  return (
    code === "return_loan_from_markaz" ||
    code === "return_loan_markaz" ||
    code === "return_loan_to_markaz" ||
    label.includes("loan return to markaz") ||
    label.includes("loan return from markaz")
  );
};

const categorySupportsQtyPrice = (categoryCode) => {
  const code = String(categoryCode || "").trim().toLowerCase();
  return code === "asset" || code === "maktaba";
};

const extractShuraNotes = (text) => {
  const match = String(text || "").match(/Shura Approval Notes\((.*?)\)/);
  return match ? match[1] : "";
};

const cleanShuraMemo = (text) => {
  return String(text || "")
    .replace(/\s*-\s*Shura Approval Notes\(.*?\)/g, "")
    .replace(/Shura Approval Notes\(.*?\)/g, "")
    .trim();
};

const EditExpenseFromCashInHand = ({
  setModal,
  transactionData,
  unitId,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const loggedInUnitId = parseInt(encryptStorage.getItem("loggedInUnitId"));
  const unitTypeName = encryptStorage.getItem("unitTypeName");

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [loading, setLoading] = useState({
    source: false,
    submit: false,
  });
  const [financeCategories, setFinanceCategories] = useState([]);
  const [expenseAmountLimit, setExpenseAmountLimit] = useState(25000);
  const shuraThreshold = unitTypeName === "Halqa" ? (expenseAmountLimit >= 50000 ? 50000 : Infinity) : 20000;
  const [formData, setFormData] = useState({
    date: new Date(),
    memo: "",
    serialNumber: "",
    entries: [
      {
        selectedCategory: null,
        financeAccount: null,
        dimension2: null,
        accounts: [],
        dimensions: [],
        amount: "",
        quantity: "",
        price: "",
        lineMemo: "",
        shuraNotes: "",
      },
    ],
  });
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [deletedFileIds, setDeletedFileIds] = useState([]);
  const [deletedDriveFileIds, setDeletedDriveFileIds] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [loadingViewId, setLoadingViewId] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const transNo = transactionData?.transactionNo || transactionData?.transNo;
    if (transNo) {
      setLoadingAttachments(true);
      ApiFinanceLocal()
        .GetTransactionAttachments(transNo)
        .then((res) => {
          if (Array.isArray(res.data)) {
            setExistingAttachments(res.data);
          }
        })
        .catch(console.error)
        .finally(() => setLoadingAttachments(false));
    }
  }, [transactionData]);

  const handleFileChange = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const MAX_SIZE = 1500 * 1024;
      const selected = [];
      const oversizedNonImages = [];

      for (const f of Array.from(e.target.files)) {
        let processedFile = f;
        if (f.type && f.type.startsWith("image/") && f.size > MAX_SIZE) {
          try {
            processedFile = await compressImageFile(f, 1400 * 1024);
          } catch (err) {
            console.error("Image compression error:", err);
          }
        }

        if (processedFile.size > MAX_SIZE) {
          oversizedNonImages.push(f.name);
        } else {
          selected.push({
            file: processedFile,
            customName: getFileNameWithoutExtension(f.name),
            id: Math.random().toString(36).substr(2, 9),
          });
        }
      }

      if (oversizedNonImages.length > 0) {
        swal({
          title: "File Size Limit Exceeded",
          text: `The following file(s) exceed the maximum size of 1500 KB (1.5 MB):\n${oversizedNonImages.join("\n")}`,
          icon: "warning",
          button: "OK",
        });
      }

      if (selected.length > 0) {
        setUploadedFiles((prev) => [...prev, ...selected]);
        setErrors((prev) => ({ ...prev, files: "" }));
      }
      e.target.value = "";
    }
  };

  const removeNewFile = (indexToRemove) => {
    setUploadedFiles((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const handleFileNameChange = (index, newName) => {
    setUploadedFiles((prev) =>
      prev.map((item, idx) => (idx === index ? { ...item, customName: newName } : item))
    );
  };

  const handleRemoveExistingAttachment = (item) => {
    const fileId = typeof item === "object" ? (item.fileId || item.id) : item;
    const driveFileId = typeof item === "object" ? (item.driveFileId || item.drive_file_id) : null;
    if (fileId) {
      setDeletedFileIds((prev) => [...prev, fileId]);
    }
    if (driveFileId) {
      setDeletedDriveFileIds((prev) => [...prev, driveFileId]);
    }
    setExistingAttachments((prev) =>
      prev.filter((a) => {
        const aId = typeof a === "object" ? (a.fileId || a.id) : a;
        return aId !== fileId;
      })
    );
  };

  const handleViewExistingAttachment = (item) => {
    if (openAttachmentInNewTab(item)) {
      return;
    }
    const fileId = typeof item === "object" ? (item.fileId || item.id) : item;
    setLoadingViewId(fileId);
    ApiFinanceLocal()
      .ViewAttachmentByFileId(fileId)
      .then((res) => {
        const contentType = res.headers?.["content-type"] || res.headers?.["Content-Type"] || "application/pdf";
        const file = new Blob([res.data], { type: contentType });
        const fileURL = URL.createObjectURL(file);
        window.open(fileURL, "_blank");
        setTimeout(() => URL.revokeObjectURL(fileURL), 1000);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => setLoadingViewId(null));
  };

  const toggle = () => setModal(false);

  const handleDrag = (e, ui) => {
    setPosition((prev) => ({
      x: prev.x + ui.deltaX,
      y: prev.y + ui.deltaY,
    }));
  };

  const handleTopLevelChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const fetchAccountsAndDimensions = (index, categoryId) => {
    if (!categoryId) return;

    ApiFinance()
      .GetFinanceSubCategoryAccountsById(categoryId)
      .then((res) => {
        setFormData((prev) => {
          const updated = prev.entries.map((entry, idx) => {
            if (idx === index) {
              const matchedAcc =
                entry.financeAccount &&
                (res.data || []).find(
                  (x) =>
                    x.value === entry.financeAccount.value ||
                    x.label === entry.financeAccount.label
                );
              return {
                ...entry,
                accounts: res.data || [],
                financeAccount: matchedAcc || res.data?.[0] || null,
              };
            }
            return entry;
          });
          return { ...prev, entries: updated };
        });
      })
      .catch(console.error);

    ApiFinance()
      .GetFinanceSubCategoryDimensionsById(categoryId)
      .then((res) => {
        setFormData((prev) => {
          const updated = prev.entries.map((entry, idx) => {
            if (idx === index) {
              const matchedDim =
                entry.dimension2 &&
                (res.data || []).find(
                  (x) =>
                    x.value === entry.dimension2.value ||
                    x.label === entry.dimension2.label
                );
              return {
                ...entry,
                dimensions: res.data || [],
                dimension2: matchedDim || res.data?.[0] || null,
              };
            }
            return entry;
          });
          return { ...prev, entries: updated };
        });
      })
      .catch(console.error);
  };

  const handleEntryChange = (index, field, value) => {
    setFormData((prev) => {
      const updatedEntries = prev.entries.map((entry, idx) => {
        if (idx === index) {
          const updated = { ...entry, [field]: value };

          if (field === "selectedCategory") {
            updated.financeAccount = null;
            updated.dimension2 = null;
            updated.accounts = [];
            updated.dimensions = [];
            const isQtyPrice = categorySupportsQtyPrice(value?.label1);
            if (!isQtyPrice) {
              updated.quantity = "";
              updated.price = "";
            }
            if (value?.value) {
              fetchAccountsAndDimensions(index, value.value);
            }
          }

          const categoryCode = updated.selectedCategory?.label1;
          if (categorySupportsQtyPrice(categoryCode)) {
            if (
              field === "quantity" ||
              field === "price" ||
              field === "selectedCategory"
            ) {
              const q = parseFloat(updated.quantity);
              const p = parseFloat(updated.price);
              if (!isNaN(q) && !isNaN(p)) {
                updated.amount = String(Math.abs(q * p));
              } else {
                updated.amount = "";
              }
            }
          }

          if (field === "amount" && value) {
            updated.amount = String(Math.abs(parseFloat(value) || 0));
          }

          return updated;
        }
        return entry;
      });

      return { ...prev, entries: updatedEntries };
    });

    setErrors((prev) => {
      const updatedEntries = Array.isArray(prev.entries) ? [...prev.entries] : [];
      if (updatedEntries[index]) {
        if (field === "selectedCategory") {
          updatedEntries[index] = {
            selectedCategory: "",
            financeAccount: "",
            dimension2: "",
            amount: "",
            quantity: "",
            price: "",
          };
        } else {
          updatedEntries[index] = {
            ...updatedEntries[index],
            [field]: "",
          };
        }
      }
      return { ...prev, entries: updatedEntries };
    });
  };

  const addEntry = () => {
    const hasSingleRowCategory = formData.entries.some((r) =>
      isSingleRowCategory(r.selectedCategory?.label1, r.selectedCategory?.label)
    );
    if (hasSingleRowCategory) {
      return;
    }
    const defaultCategory = formData.entries[0]?.selectedCategory || financeCategories[0] || null;
    const nextIndex = formData.entries.length;
    setFormData((prev) => ({
      ...prev,
      entries: [
        ...prev.entries,
        {
          isNew: true,
          selectedCategory: defaultCategory,
          financeAccount: null,
          dimension2: null,
          accounts: [],
          dimensions: [],
          amount: "",
          quantity: "",
          price: "",
          lineMemo: "",
        },
      ],
    }));
    setErrors((prev) => {
      if (Array.isArray(prev.entries)) {
        return {
          ...prev,
          entries: [...prev.entries, {}],
        };
      }
      return prev;
    });
    if (defaultCategory?.value) {
      fetchAccountsAndDimensions(nextIndex, defaultCategory.value);
    }
  };

  const removeEntry = (index) => {
    setFormData((prev) => ({
      ...prev,
      entries: prev.entries.filter((_, idx) => idx !== index),
    }));
    setErrors((prev) => {
      if (Array.isArray(prev.entries)) {
        return {
          ...prev,
          entries: prev.entries.filter((_, idx) => idx !== index),
        };
      }
      return prev;
    });
  };

  const getTotalAmount = (entries) => {
    return entries.reduce((sum, entry) => sum + (parseFloat(entry.amount) || 0), 0);
  };

  const isBusy = Object.values(loading).some(Boolean);

  useEffect(() => {
    ApiFinance()
      .GetExpenseAmountLimit()
      .then((res) => {
        const limit = parseFloat(res.data);
        if (!isNaN(limit)) {
          setExpenseAmountLimit(limit);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch expense amount limit", err);
      });
  }, []);

  // Fetch Categories
  useEffect(() => {
    setLoading((prev) => ({ ...prev, source: true }));
    ApiFinance()
      .GetFinanceSubCategoriesListByType("expense")
      .then((res) => {
        const categories = (res.data || []).filter((category) => {
          if (
            category.label1 === "maktaba_payment_to_markaz" ||
            category.label1 === "maktaba_payable" ||
            category.label1 === "nida_payment_to_markaz" ||
            category.label1 === "nida_payable" ||
            category.label1 === "return_loan_from_markaz" ||
            category.label1 === "return_loan_markaz" ||
            category.label1 === "return_loan_to_markaz" ||
            category.label1 === "return_loan_individual" ||
            (category.label || category.name || category.category || "").toLowerCase().includes("return loan to markaz") ||
            (category.label || category.name || category.category || "").toLowerCase().includes("loan return to markaz") ||
            (category.label || category.name || category.category || "").toLowerCase().includes("loan return from markaz") ||
            (category.label || category.name || category.category || "").toLowerCase().includes("return loan to individual") ||
            (category.label || category.name || category.category || "").toLowerCase().includes("loan return to individual") ||
            (category.label || category.name || category.category || "").toLowerCase().includes("loan return from individual") ||
            (category.label || category.name || category.category || "").toLowerCase().includes("return loan from individual")
          ) {
            return false;
          }
          if (category.label1 === "maktaba_expense") {
            if (unitTypeName !== "Tanzeem") {
              return false;
            }
          } else if (
            unitTypeName !== "Halqa" &&
            (category.label1?.toLowerCase().includes("maktaba") ||
              (category.label || category.name || category.category || "").toLowerCase().includes("maktaba"))
          ) {
            return false;
          }
          return true;
        });
        const formatted = categories.map((c) => ({
          value: c.value || c.id,
          label: c.label || c.category,
          label1: c.label1 || c.code,
        }));
        setFinanceCategories(formatted);
      })
      .catch(console.error)
      .finally(() => {
        setLoading((prev) => ({ ...prev, source: false }));
      });
  }, [unitTypeName]);

  // Load existing transaction
  useEffect(() => {
    if (!transactionData || financeCategories.length === 0) return;

    const filteredDetails = (transactionData.details || []).filter(
      (item) => (item.amount || 0) > 0
    );

    if (filteredDetails.length > 0) {
      const entriesData = filteredDetails.map((detail) => {
        const memoParts = parseExpenseMemo(detail.memo);
        const rawLabel = String(detail.category?.label || detail.category?.category || "").replace(/\s*\([^)]*\)/, "").trim();
        const mappedCat = detail.category
          ? {
              value: detail.category.value || detail.category.id,
              label: rawLabel,
              label1: detail.category.label1 || detail.category.code,
            }
          : findOption(financeCategories, [detail.categoryID, detail.categoryId]) ||
            financeCategories[0] ||
            null;

        return {
          id: detail.id || null,
          selectedCategory: mappedCat,
          financeAccount: detail.account
            ? { value: parseInt(detail.account), label: detail.accountName }
            : null,
          dimension2: detail.dimension2Id
            ? { value: parseInt(detail.dimension2Id), label: detail.dimension2Name }
            : null,
          accounts: [],
          dimensions: [],
          amount: String(Math.abs(detail.amount || 0)),
          quantity: detail.quantity !== undefined && detail.quantity !== null ? String(detail.quantity) : (memoParts.quantity || ""),
          price: detail.price !== undefined && detail.price !== null ? String(detail.price) : (memoParts.price || ""),
          lineMemo: cleanShuraMemo(memoParts.memo),
          shuraNotes: extractShuraNotes(detail.memo) || extractShuraNotes(transactionData.memo) || "",
          serialNumber: cleanSerialNumber(detail.personID || detail.personId || detail.serialNumber || transactionData?.personID || transactionData?.personId || transactionData?.serialNumber),
        };
      });

      const mainMemoParsed = parseExpenseMemo(transactionData.memo);

      setFormData({
        date: getDateValue(transactionData.date),
        memo: cleanShuraMemo(mainMemoParsed.memo),
        serialNumber: cleanSerialNumber(transactionData?.personID || transactionData?.personId || transactionData?.serialNumber),
        entries: entriesData,
      });

      entriesData.forEach((entry, index) => {
        if (entry.selectedCategory?.value) {
          fetchAccountsAndDimensions(index, entry.selectedCategory.value);
        }
      });
    } else {
      const memoParts = parseExpenseMemo(transactionData.memo);
      const mappedCat = transactionData.category
        ? {
            value: transactionData.category.value || transactionData.category.id,
            label: transactionData.category.label || transactionData.category.category,
            label1:
              transactionData.category.label1 || transactionData.category.code,
          }
        : findOption(financeCategories, [
            transactionData.categoryId,
            transactionData.categoryID,
          ]) ||
          financeCategories[0] ||
          null;

      const singleEntry = {
        selectedCategory: mappedCat,
        financeAccount: transactionData.codeId
          ? { value: parseInt(transactionData.codeId), label: transactionData.accountName }
          : null,
        dimension2: transactionData.dimension2Id
          ? { value: parseInt(transactionData.dimension2Id), label: transactionData.subHeadName }
          : null,
        accounts: [],
        dimensions: [],
        amount: getAmount(transactionData.amount),
        quantity: transactionData.quantity !== undefined && transactionData.quantity !== null ? String(transactionData.quantity) : (memoParts.quantity || ""),
        price: transactionData.price !== undefined && transactionData.price !== null ? String(transactionData.price) : (memoParts.price || ""),
        lineMemo: cleanShuraMemo(memoParts.memo),
        shuraNotes: extractShuraNotes(transactionData.memo) || "",
        serialNumber: cleanSerialNumber(transactionData?.personID || transactionData?.personId || transactionData?.serialNumber),
      };

      setFormData({
        date: getDateValue(transactionData.date),
        memo: cleanShuraMemo(memoParts.memo),
        serialNumber: cleanSerialNumber(transactionData?.personID || transactionData?.personId || transactionData?.serialNumber),
        entries: [singleEntry],
      });

      if (singleEntry.selectedCategory?.value) {
        fetchAccountsAndDimensions(0, singleEntry.selectedCategory.value);
      }
    }
  }, [transactionData, financeCategories]);

  const validate = () => {
    const temp = {};
    const entriesErrors = [];
    let hasInvalidEntry = false;

    const totalAmount = getTotalAmount(formData.entries);
    if (totalAmount > expenseAmountLimit) {
      temp.amount = `${t("you_cannot_spend_above_this_amount_in_cash_in_hand")} (${expenseAmountLimit})`;
    }

    const hasLoanIndividual = formData.entries.some(
      (e) => e.selectedCategory?.label1 === "return_loan_individual"
    );
    if (hasLoanIndividual && !formData.serialNumber) {
      temp.serialNumber = t("common_field_required_error");
    }

    for (let i = 0; i < formData.entries.length; i++) {
      const entry = formData.entries[i];
      const entryErrors = {};

      if (!entry.selectedCategory) {
        entryErrors.selectedCategory = t("common_field_required_error");
        hasInvalidEntry = true;
      } else {
        const categoryCode = entry.selectedCategory.label1;
        const isSpecial = isSpecialCategory(categoryCode);
        const isQtyPrice = categorySupportsQtyPrice(categoryCode);

        if (!isQtyPrice) {
          if (!entry.amount) {
            entryErrors.amount = t("common_field_required_error");
            hasInvalidEntry = true;
          } else if (parseFloat(entry.amount) <= 0) {
            entryErrors.amount = t("amount_must_be_greater_than_zero");
            hasInvalidEntry = true;
          }
        } else {
          if (!entry.quantity) {
            entryErrors.quantity = t("common_field_required_error");
            hasInvalidEntry = true;
          } else if (parseFloat(entry.quantity) <= 0) {
            entryErrors.quantity = t("amount_must_be_greater_than_zero");
            hasInvalidEntry = true;
          }

          if (!entry.price) {
            entryErrors.price = t("common_field_required_error");
            hasInvalidEntry = true;
          } else if (parseFloat(entry.price) <= 0) {
            entryErrors.price = t("amount_must_be_greater_than_zero");
            hasInvalidEntry = true;
          }
        }

        if (!isSpecial) {
          if (!entry.financeAccount) {
            entryErrors.financeAccount = t("common_field_required_error");
            hasInvalidEntry = true;
          }
          if (
            categoryCode !== "tarbiati_course" &&
            categoryCode !== "misc" &&
            !entry.dimension2
          ) {
            entryErrors.dimension2 = t("common_field_required_error");
            hasInvalidEntry = true;
          }
        }

        if ((parseFloat(entry.amount) || 0) >= shuraThreshold) {
          if (!entry.shuraNotes || !entry.shuraNotes.trim()) {
            entryErrors.shuraNotes = t("common_field_required_error");
            hasInvalidEntry = true;
          }
        }
      }

      entriesErrors.push(entryErrors);
    }

    if (!formData.memo || !formData.memo.trim()) {
      temp.memo = t("common_field_required_error");
      hasInvalidEntry = true;
    }

    const dateError = validateTransactionDate(formData.date, transactionData?.date);
    if (dateError) {
      temp.date = t(dateError);
    }

    temp.entries = hasInvalidEntry ? entriesErrors : [];
    setErrors(temp);

    return (
      Object.keys(temp).filter((k) => k !== "entries").length === 0 &&
      !hasInvalidEntry
    );
  };

  const handleSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!validate()) return;

    const loanRow = formData.entries.find(e =>
      e.selectedCategory?.label1 === "return_loan_individual" ||
      e.selectedCategory?.label1 === "loan_individual"
    );
    const serialNumberToUse = loanRow?.serialNumber || formData.serialNumber || "";

    const entriesPayload = formData.entries.map((entry) => {
      const isQtyPrice = categorySupportsQtyPrice(entry.selectedCategory?.label1);
      const parsedAmount = parseFloat(entry.amount) || 0;
      let fallbackMemo = cleanShuraMemo(entry.lineMemo || formData.memo || "");
      if (parsedAmount >= shuraThreshold && entry.shuraNotes?.trim()) {
        fallbackMemo = fallbackMemo
          ? `${fallbackMemo} - Shura Approval Notes(${entry.shuraNotes.trim()})`
          : `Shura Approval Notes(${entry.shuraNotes.trim()})`;
      }
      return {
        financeAccount: entry.financeAccount,
        dimension2: entry.dimension2,
        category: entry.selectedCategory,
        amount: parsedAmount,
        lineMemo: fallbackMemo,
        quantity: isQtyPrice && entry.quantity ? parseInt(entry.quantity, 10) : null,
        price: isQtyPrice && entry.price ? parseFloat(entry.price) : null,
        serialNumber: entry.serialNumber || serialNumberToUse,
      };
    });

    let finalMemo = cleanShuraMemo(formData.memo);
    const firstShuraRow = formData.entries.find(e => (parseFloat(e.amount) || 0) >= shuraThreshold && e.shuraNotes?.trim());
    if (firstShuraRow) {
      finalMemo = finalMemo
        ? `${finalMemo} - Shura Approval Notes(${firstShuraRow.shuraNotes.trim()})`
        : `Shura Approval Notes(${firstShuraRow.shuraNotes.trim()})`;
    }

    const payload = {
      category: entriesPayload[0]?.category ?? null,
      financeAccount: entriesPayload[0]?.financeAccount ?? null,
      dimension2: entriesPayload[0]?.dimension2 ?? null,
      date: formData.date,
      amount: getTotalAmount(formData.entries),
      serialNumber: serialNumberToUse,
      memo: finalMemo,
      unitId: unitId || transactionData?.unitId || loggedInUnitId,
      transactionNo: transactionData?.transactionNo,
      reference: transactionData?.reference,
      entries: entriesPayload,
      quantity: entriesPayload[0]?.quantity ?? null,
      price: entriesPayload[0]?.price ?? null,
    };

    if (entriesPayload.length === 1) {
      payload.memo = entriesPayload[0].lineMemo;
    }

    const formDataObj = new FormData();
    formDataObj.append("date", formData.date ? new Date(formData.date).toISOString() : new Date().toISOString());
    formDataObj.append("amount", payload.amount);
    formDataObj.append("serialNumber", serialNumberToUse || "");
    formDataObj.append("memo", payload.memo || "");
    formDataObj.append("unitId", payload.unitId);
    formDataObj.append("transactionNo", payload.transactionNo);
    formDataObj.append("reference", payload.reference || "");
    if (payload.quantity !== null && payload.quantity !== undefined) formDataObj.append("quantity", payload.quantity);
    if (payload.price !== null && payload.price !== undefined) formDataObj.append("price", payload.price);

    if (payload.category) {
      if (payload.category.value) formDataObj.append("category.value", payload.category.value);
      if (payload.category.label) formDataObj.append("category.label", payload.category.label);
      if (payload.category.label1) formDataObj.append("category.label1", payload.category.label1);
    }
    if (payload.financeAccount) {
      if (payload.financeAccount.value) formDataObj.append("financeAccount.value", payload.financeAccount.value);
      if (payload.financeAccount.label) formDataObj.append("financeAccount.label", payload.financeAccount.label);
    }
    if (payload.dimension2) {
      if (payload.dimension2.value) formDataObj.append("dimension2.value", payload.dimension2.value);
      if (payload.dimension2.label) formDataObj.append("dimension2.label", payload.dimension2.label);
    }

    entriesPayload.forEach((entry, idx) => {
      if (entry.financeAccount) {
        if (entry.financeAccount.value) formDataObj.append(`entries[${idx}].financeAccount.value`, entry.financeAccount.value);
        if (entry.financeAccount.label) formDataObj.append(`entries[${idx}].financeAccount.label`, entry.financeAccount.label);
      }
      if (entry.dimension2) {
        if (entry.dimension2.value) formDataObj.append(`entries[${idx}].dimension2.value`, entry.dimension2.value);
        if (entry.dimension2.label) formDataObj.append(`entries[${idx}].dimension2.label`, entry.dimension2.label);
      }
      if (entry.category) {
        if (entry.category.value) formDataObj.append(`entries[${idx}].category.value`, entry.category.value);
        if (entry.category.label) formDataObj.append(`entries[${idx}].category.label`, entry.category.label);
        if (entry.category.label1) formDataObj.append(`entries[${idx}].category.label1`, entry.category.label1);
      }
      formDataObj.append(`entries[${idx}].amount`, entry.amount);
      formDataObj.append(`entries[${idx}].lineMemo`, entry.lineMemo || "");
      formDataObj.append(`entries[${idx}].serialNumber`, entry.serialNumber || "");
      if (entry.quantity !== null && entry.quantity !== undefined) {
        formDataObj.append(`entries[${idx}].quantity`, entry.quantity);
      }
      if (entry.price !== null && entry.price !== undefined) {
        formDataObj.append(`entries[${idx}].price`, entry.price);
      }
    });

    deletedFileIds.forEach((id, idx) => {
      formDataObj.append(`deleted_file_ids[${idx}]`, id);
      formDataObj.append("deleted_file_ids", id);
    });
    deletedDriveFileIds.forEach((id, idx) => {
      formDataObj.append(`deleted_drive_file_ids[${idx}]`, id);
      formDataObj.append("deleted_drive_file_ids", id);
    });

    existingAttachments.forEach((att, idx) => {
      if (typeof att === "object") {
        if (att.driveFileId || att.drive_file_id) {
          formDataObj.append(`kept_drive_files[${idx}].drive_file_id`, att.driveFileId || att.drive_file_id);
        }
        if (att.fileName || att.file_name) {
          formDataObj.append(`kept_drive_files[${idx}].file_name`, att.fileName || att.file_name);
        }
        if (att.webViewLink || att.web_view_link) {
          formDataObj.append(`kept_drive_files[${idx}].web_view_link`, att.webViewLink || att.web_view_link);
        }
      }
    });

    uploadedFiles.forEach((item) => {
      let fileName = item.customName?.trim() || item.file.name;
      const ext = item.file.name.split(".").pop();
      if (ext && !fileName.toLowerCase().endsWith(`.${ext.toLowerCase()}`)) {
        fileName = `${fileName}.${ext}`;
      }
      formDataObj.append("files", item.file, fileName);
    });

    setLoading((prev) => ({ ...prev, submit: true }));
    ApiFinanceLocal()
      .EditExpense(formDataObj)
      .then((res) => {
        const data = res.data || {};

        if (Number(data.statusCode) !== 200) {
          const isWarning = Number(data.statusCode) === 300 || data.message === "modification_request_already_pending";
          swal({
            title: isWarning ? t("common_warning") || "Warning" : "Error",
            text: t(data.message) || data.message || "Failed to edit Expense",
            icon: isWarning ? "warning" : "error",
            buttons: "OK",
          }).then(() => {
            if (isWarning) {
              setModal(false);
            }
          });
          return;
        }

        let successText = t(data.message) || t("funds_expense_updated_successfully");
        if (data.warning) {
          successText += `\n\n${t(data.warning) || data.warning}`;
        }

        swal({
          title: data.warning ? (t("warning") || "Warning") : (t("Success") || "Success"),
          text: successText,
          icon: data.warning ? "warning" : "success",
          buttons: "OK",
        }).then(() => {
          setModal(false);
          if (typeof onSuccess === "function") {
            onSuccess();
          }
        });
      })
      .catch((err) => {
        console.error(err);
        swal({
          title: "Error",
          text: err.response?.data || "Unable to update expense.",
          icon: "error",
          buttons: "OK",
        });
      })
      .finally(() => {
        setLoading((prev) => ({ ...prev, submit: false }));
      });
  };

  const hasSingleRowCategory = formData.entries.some((r) =>
    isSingleRowCategory(r.selectedCategory?.label1, r.selectedCategory?.label)
  );
  const totalRows = formData.entries.length;
  const isInvalidMultiRowState = hasSingleRowCategory && totalRows > 1;
  const singleRowWarningKey = "loan_return_markaz_single_record_warning";

  return (
    <Draggable position={position} onDrag={handleDrag} handle=".modal-header" cancel=".no-drag">
      <Modal isOpen className="inmodal" size="xl" style={{ maxWidth: "90%", width: "1500px" }} autoFocus={false}>
        <ModalHeader className="modal-title" tag="h4">
          <GetLanguageString props="finance_expense_cash_in_hand" />
        </ModalHeader>

        {loading.source || loading.submit ? (
          <ModalBody className="text-center">
            <LoadingSpinner />
          </ModalBody>
        ) : (
          <Form onSubmit={handleSubmit} noValidate>
            <ModalBody>
              <Row className="mb-3">
                <Col md="4">
                  <FormGroup>
                    <Label className="col-form-label">
                      <GetLanguageString props="funds_date" />
                    </Label>
                    <DatePicker
                      showYearDropdown
                      dropdownMode="select"
                      placeholderText="dd/mm/yyyy"
                      className="form-control no-drag"
                      selected={formData.date}
                      minDate={getMinMaxDates(formData.date, transactionData?.date).minDate}
                      maxDate={getMinMaxDates(formData.date, transactionData?.date).maxDate}
                      onChange={(date) =>
                        handleTopLevelChange("date", date ?? new Date())
                      }
                      dateFormat="dd/MM/yyyy"
                    />
                    {errors.date && (
                      <h4 className="text-error">{errors.date}</h4>
                    )}
                  </FormGroup>
                </Col>

                <Col md="4">
                  <CustomInput
                    name="memo"
                    label="transactions_memo"
                    value={formData.memo}
                    onChange={(e) => handleTopLevelChange("memo", e.target.value)}
                    error={errors.memo}
                  />
                </Col>

                <Col md="4" className="d-flex align-items-end">
                  <FormGroup style={{ width: "100%" }}>
                    <Label className="col-form-label">
                      <strong>
                        <GetLanguageString props="funds_add_fund_amount" />
                      </strong>
                    </Label>
                    <Input
                      type="text"
                      className="no-drag font-weight-bold"
                      disabled
                      value={new Intl.NumberFormat().format(
                        getTotalAmount(formData.entries)
                      )}
                    />
                    {errors.amount && <h4 className="text-error">{errors.amount}</h4>}
                  </FormGroup>
                </Col>
              </Row>

              {/* Attachments Section */}
              <div className="mb-4">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <Label className="col-form-label mb-0 fw-bold" style={{ fontSize: "14px", color: "#2f4050" }}>
                    <i className="fa fa-paperclip me-2 text-primary"></i>
                    <GetLanguageString props="attached_files" /> / <GetLanguageString props="upload_file" />
                  </Label>
                  {loadingAttachments && <Spinner size="sm" color="primary" />}
                </div>

                {/* Existing Attachments */}
                {existingAttachments.length > 0 && (
                  <div
                    className="p-3 mb-3 bg-light rounded"
                    style={{ border: "1px solid #e7eaec", borderLeft: "4px solid #1ab394" }}
                  >
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="fw-bold" style={{ color: "#2f4050", fontSize: "12px" }}>
                        <GetLanguageString props="attached_files" /> ({existingAttachments.length})
                      </span>
                    </div>
                    <div className="d-flex flex-column gap-2">
                      {existingAttachments.map((item, index) => {
                        const fileId = typeof item === "object" ? (item.fileId || item.id) : item;
                        const displayName = (typeof item === "object" && item.fileName) ? getFileNameWithoutExtension(item.fileName) : `Attachment ${index + 1}`;
                        return (
                          <div
                            key={fileId || index}
                            className="d-flex justify-content-between align-items-center p-2 rounded border bg-white"
                            style={{ borderColor: "#E5E7EB" }}
                          >
                            <div className="d-flex align-items-center gap-2">
                              <i className="fa fa-file text-muted"></i>
                              <span style={{ fontSize: "13px", fontWeight: "500", color: "#374151" }}>
                                {displayName}
                              </span>
                            </div>
                            <div className="d-flex gap-2">
                              <Button
                                color="white"
                                size="sm"
                                style={{ padding: "4px 10px", fontSize: "12px", borderRadius: "6px", display: "inline-flex", alignItems: "center", gap: "6px", border: "1px solid #D1D5DB" }}
                                disabled={loadingViewId === fileId}
                                onClick={() => handleViewExistingAttachment(item)}
                                type="button"
                              >
                                {loadingViewId === fileId ? (
                                  <Spinner size="sm" style={{ width: "12px", height: "12px" }} />
                                ) : (
                                  <i className="fa fa-eye text-primary"></i>
                                )}
                                <GetLanguageString props="member_add_rafiq_request_view_document" />
                              </Button>
                              <Button
                                color="white"
                                size="sm"
                                style={{ padding: "4px 10px", fontSize: "12px", borderRadius: "6px", display: "inline-flex", alignItems: "center", gap: "6px", border: "1px solid #fee2e2", color: "#dc2626" }}
                                onClick={() => handleRemoveExistingAttachment(item)}
                                type="button"
                              >
                                <i className="fa fa-trash text-danger"></i>
                                <GetLanguageString props="common_delete" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Upload New Files */}
                <Row className="mb-2">
                  <Col md="6">
                    <FormGroup className="mb-0">
                      <Label className="col-form-label" style={{ fontSize: "12px" }}>
                        + <GetLanguageString props="upload_file" />
                      </Label>
                      <Input
                        type="file"
                        multiple
                        name="files"
                        onChange={handleFileChange}
                        className="no-drag"
                        accept="image/*,application/pdf"
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6" className="d-flex align-items-end">
                    <div
                      className="p-2 rounded w-100"
                      style={{
                        backgroundColor: "#f0fdf4",
                        border: "1px solid #bbf7d0",
                        fontSize: "12px",
                        color: "#166534",
                        minHeight: "42px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <div className="d-flex align-items-start gap-2">
                        <i className="fa fa-info-circle mt-1" style={{ color: "#16a34a", fontSize: "14px" }}></i>
                        <div>
                          <div><GetLanguageString props="expense_multi_file_instruction" /></div>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>

                {/* New Uploaded Files List */}
                {uploadedFiles.length > 0 && (
                  <div
                    className="p-3 mt-2 bg-light rounded"
                    style={{ border: "1px solid #e7eaec", borderLeft: "4px solid #3b82f6" }}
                  >
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="fw-bold" style={{ color: "#2f4050", fontSize: "12px" }}>
                        <i className="fa fa-plus-circle me-1 text-info"></i>
                        New Files ({uploadedFiles.length})
                      </span>
                    </div>
                    <div className="d-flex flex-column gap-2">
                      {uploadedFiles.map((item, i) => (
                        <div
                          key={item.id || i}
                          className="d-flex align-items-center gap-3 p-2 bg-white rounded border"
                          style={{ fontSize: "13px" }}
                        >
                          <span
                            className="badge bg-secondary text-white"
                            style={{ minWidth: "24px", height: "24px", display: "flex", alignItems: "center", justifyContent: "center" }}
                          >
                            {i + 1}
                          </span>
                          <div className="flex-grow-1">
                            <Input
                              type="text"
                              value={item.customName}
                              onChange={(e) => handleFileNameChange(i, e.target.value)}
                              placeholder={t("member_document_name")}
                              className="form-control-sm no-drag"
                              style={{ fontSize: "12px" }}
                            />
                          </div>
                          <span className="text-muted" style={{ fontSize: "11px", whiteSpace: "nowrap" }}>
                            {(item.file.size / 1024).toFixed(0)} KB
                          </span>
                          <Button
                            color="white"
                            size="sm"
                            style={{ padding: "4px 8px", border: "1px solid #fee2e2", color: "#dc2626" }}
                            onClick={() => removeNewFile(i)}
                            type="button"
                          >
                            <i className="fa fa-trash text-danger"></i>
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Entries Box */}
              <div className="ibox">
                <div className="ibox-title d-flex justify-content-between align-items-center">
                  <h5>
                    <GetLanguageString props="finance_other_expense_entry_details" />
                  </h5>
                  {hasSingleRowCategory ? (
                    <MdOutlineAddCircleOutline
                      size={25}
                      color="#94a3b8"
                      style={{ marginRight: 6, marginBottom: 1, cursor: "not-allowed", opacity: 0.5 }}
                      title={t(singleRowWarningKey)}
                    />
                  ) : (
                    <MdOutlineAddCircleOutline
                      size={25}
                      color="#3ba147"
                      onClick={addEntry}
                      style={{ marginRight: 6, marginBottom: 1, cursor: "pointer" }}
                    />
                  )}
                </div>
                <div className="ibox-content">
                  {isInvalidMultiRowState && (
                    <div
                      className="alert alert-danger mb-3 p-3 font-weight-bold"
                      style={{
                        backgroundColor: "#fef2f2",
                        color: "#991b1b",
                        border: "1px solid #f87171",
                        borderRadius: "6px",
                      }}
                    >
                      <GetLanguageString props={singleRowWarningKey} />
                    </div>
                  )}
                  {formData.entries.map((entry, index) => {
                    const categoryCode = entry.selectedCategory?.label1;
                    const isLoanIndividual =
                      categoryCode === "return_loan_individual" ||
                      categoryCode === "loan_individual";
                    const isAssetOrMaktaba = categoryCode === "asset" || categoryCode === "maktaba";
                    const isSpecial = isSpecialCategory(categoryCode);
                    const isQtyPrice = categorySupportsQtyPrice(categoryCode);
                    const isConflictingRow = isInvalidMultiRowState && !isSingleRowCategory(categoryCode, entry.selectedCategory?.label);

                    return (
                      <Row
                        key={index}
                        className={`mb-3 align-items-start ${isConflictingRow ? "bg-red-50 highlight-error" : ""}`}
                        style={
                          isConflictingRow
                            ? {
                                backgroundColor: "#fef2f2",
                                border: "1px solid #fca5a5",
                                borderRadius: "6px",
                                padding: "8px 4px",
                                marginBottom: "12px",
                              }
                            : undefined
                        }
                      >
                        <Col md>
                          <div
                            onClick={() => {
                              if (!entry.isNew) {
                                swal({
                                  title: t("finance_category_change_notice_title") || "Change Category",
                                  text: t("finance_category_change_notice_text") || "To change the category of an existing entry, please delete this row and add a new row with your desired category.",
                                  icon: "info",
                                  buttons: "OK",
                                });
                              }
                            }}
                          >
                            <CustomSelect
                              name={`category-${index}`}
                              label="finance_tab_category"
                              options={financeCategories}
                              value={entry.selectedCategory}
                              onChange={(val) =>
                                handleEntryChange(index, "selectedCategory", val)
                              }
                              error={errors.entries?.[index]?.selectedCategory}
                              isDisabled={!entry.isNew}
                              placeholder={t("member_select")}
                            />
                          </div>
                        </Col>

                        {isLoanIndividual && (
                          <Col md>
                            <CustomInput
                              name={`serialNumber-${index}`}
                              label="notifications_rafiq_serial_number"
                              value={entry.serialNumber || ""}
                              onChange={(e) =>
                                handleEntryChange(index, "serialNumber", e.target.value)
                              }
                              error={errors.entries?.[index]?.serialNumber}
                            />
                          </Col>
                        )}

                        {!isSpecial && (
                          <Col md>
                            <CustomSelect
                              name={`financeAccount-${index}`}
                              label="funds_expense_head"
                              options={entry.accounts || []}
                              value={entry.financeAccount}
                              onChange={(val) =>
                                handleEntryChange(index, "financeAccount", val)
                              }
                              error={errors.entries?.[index]?.financeAccount}
                              isDisabled={!entry.selectedCategory}
                              placeholder={
                                !entry.selectedCategory
                                  ? t("finance_expense_heads_select")
                                  : (entry.accounts || []).length > 0
                                    ? t("finance_expense_heads_select")
                                    : t("finance_expense_heads_empty")
                              }
                            />
                          </Col>
                        )}

                        {!isSpecial && (
                          <Col md>
                            <CustomSelect
                              name={`dimension2-${index}`}
                              label="funds_expense_sub_head"
                              options={entry.dimensions || []}
                              value={entry.dimension2}
                              onChange={(val) =>
                                handleEntryChange(index, "dimension2", val)
                              }
                              error={errors.entries?.[index]?.dimension2}
                              isDisabled={!entry.selectedCategory}
                              placeholder={
                                !entry.selectedCategory
                                  ? t("finance_expense_sub_heads_select")
                                  : (entry.dimensions || []).length > 0
                                    ? t("finance_expense_sub_heads_select")
                                    : t("finance_expense_sub_heads_empty")
                              }
                            />
                          </Col>
                        )}

                        <Col md={isAssetOrMaktaba ? "1" : null}>
                          <CustomInput
                            name={`entryAmount-${index}`}
                            label="funds_add_fund_amount"
                            value={entry.amount}
                            onChange={(e) =>
                              handleEntryChange(index, "amount", e.target.value)
                            }
                            error={errors.entries?.[index]?.amount}
                            readOnly={isQtyPrice}
                            disabled={isQtyPrice}
                          />
                        </Col>

                        {isAssetOrMaktaba && (
                          <Col md="1">
                            <CustomInput
                              name={`quantity-${index}`}
                              label="finance_other_income_quantity"
                              value={entry.quantity}
                              onChange={(e) =>
                                handleEntryChange(index, "quantity", e.target.value)
                              }
                              error={errors.entries?.[index]?.quantity}
                            />
                          </Col>
                        )}

                        {isAssetOrMaktaba && (
                          <Col md="1">
                            <CustomInput
                              name={`price-${index}`}
                              label="finance_other_income_price"
                              value={entry.price}
                              onChange={(e) =>
                                handleEntryChange(index, "price", e.target.value)
                              }
                              error={errors.entries?.[index]?.price}
                            />
                          </Col>
                        )}

                        <Col md>
                          <CustomInput
                            name={`lineMemo-${index}`}
                            label="transactions_memo"
                            value={entry.lineMemo}
                            onChange={(e) =>
                              handleEntryChange(index, "lineMemo", e.target.value)
                            }
                            error={errors.entries?.[index]?.lineMemo}
                          />
                        </Col>
                        {(parseFloat(entry.amount) || 0) >= shuraThreshold && (
                          <Col md>
                            <CustomInput
                              name={`shuraNotes-${index}`}
                              label="shura_approval_notes"
                              value={entry.shuraNotes || ""}
                              onChange={(e) =>
                                handleEntryChange(index, "shuraNotes", e.target.value)
                              }
                              error={errors.entries?.[index]?.shuraNotes}
                              placeholder={t("enter_shura_approval_notes")}
                            />
                          </Col>
                        )}

                        <Col md="1" className="text-center">
                          <Label className="col-form-label d-block">&nbsp;</Label>
                          {formData.entries.length === 1 ? (
                            <TbTrashOff
                              size={25}
                              color="#a72424"
                              style={{ opacity: 0.5 }}
                            />
                          ) : (
                            <TbTrash
                              size={25}
                              color="#bb0e0e"
                              style={{ cursor: "pointer" }}
                              onClick={() => removeEntry(index)}
                            />
                          )}
                        </Col>
                      </Row>
                    );
                  })}
                </div>
              </div>
            </ModalBody>

            <ModalFooter>
              {CheckPermission("CanAddFunds") ? (
                <>
                  <Button
                    color="white"
                    className={GetCustomStyles().btn_style}
                    onClick={toggle}
                  >
                    <GetLanguageString props="common_cancel" />
                  </Button>
                  <Button
                    color="primary"
                    className={GetCustomStyles().btn_style}
                    disabled={isBusy || isInvalidMultiRowState}
                    type="submit"
                  >
                    <GetLanguageString props="common_save_changes" />
                  </Button>
                </>
              ) : null}
            </ModalFooter>
          </Form>
        )}
      </Modal>
    </Draggable>
  );
};

export default EditExpenseFromCashInHand;
